import {html, LitElement} from 'lit';
import {customElement, property, queryAssignedElements} from 'lit/decorators.js';
import {Cookies, Logger, PubSub, Tracking2} from '@mamdev/cad.static-next-lib';
import {Url} from '../../ts/services/url';
import UrlPath from '../../ts/services/url-path';

import styles from './region.styles';
import elementsStyles from '../../ts/css/elements.styles';
import generalStyles from '../../ts/css/general.styles';
import {resetStyles} from '../../ts/css/resets.styles';
import {iconsFont} from '../../ts/css/icons.styles';

@customElement('ui-region')
export class Region extends LitElement {
  static readonly styles = [resetStyles, generalStyles, elementsStyles, iconsFont, styles];

  @property({reflect: true}) isDropdownOnly: 'true' | 'false' = 'false';
  @property() regionFlagKey = 'DE-11';
  @property() target?: string;
  @queryAssignedElements({slot: 'options', flatten: false}) select: HTMLSelectElement[];
  @queryAssignedElements({slot: 'location', flatten: false}) locationSlot: HTMLSelectElement[];

  private isKeyboardArrow = false;
  private optShown = false;

  private readonly name = 'region';
  private readonly locationCookieName = 'loc_news';
  private readonly flagIdMap_: Record<string, string> = {
    'DE-01': 'sh',
    'DE-02': 'hh',
    'DE-03': 'ni',
    'DE-04': 'hb',
    'DE-05': 'nrw',
    'DE-06': 'he',
    'DE-07': 'rp',
    'DE-08': 'bw',
    'DE-09': 'by',
    'DE-10': 'sl',
    'DE-11': 'b',
    'DE-12': 'bb',
    'DE-13': 'mv',
    'DE-14': 'sn',
    'DE-15': 'sa',
    'DE-16': 'th',
    'DE-09162': 'm'
  };

  connectedCallback(): void {
    super.connectedCallback();
    this.handleRegionCookieUpdate();
    this.onClickTeaserAllowTracking();
  }

  render() {
    const dropdownOnly = this.isDropdownOnly == 'true';
    return html`
      <div>
          <slot name="header"></slot>
          <div class="region-container">
            <div class="region__select-wrap">
            ${dropdownOnly ? html`
            <div class="region__img-container">
              <div class="region__dropdown-img"></div>
            </div>
              <slot name="heading"></slot>
            ` : null}
             ${!dropdownOnly ? html`<slot name="location" @slotchange="${this.handleLocationSlotChange}"></slot>` : null}
            <div class="region__select-ic-wrap">
              <span class="region__select-ic">
              <slot name="options"  @slotchange="${this.handleSelectSlotChange}">
              </slot>
              </span>
           </div>
            </div>
            <slot name="teaser"></slot>
        </div>
      </div>
    `;
  }

  handleSelectSlotChange() {
    const selectElem = this.select[0];
    selectElem.addEventListener('click', this.clickHandler.bind(this));
    selectElem.addEventListener('focus', this.focusHandler.bind(this));
    selectElem.addEventListener('change', this.changeHandler.bind(this));
    selectElem.addEventListener('keydown', this.keydownHandler.bind(this));
  }

  /** Initially show flag of the region based on data-region-key */
  handleLocationSlotChange() {
    const slotContainer = this.locationSlot[0];
    const flagContainer = slotContainer.querySelector('.region__flag');
    if (flagContainer) {
      const flagFilePath = `${UrlPath.moduleAssets}/${this.name}/${this.flagIdMap_[this.regionFlagKey]}.svg`;
      const flagImage = new Image();
      flagImage.src = flagFilePath;
      flagContainer.appendChild(flagImage);
    }
  }

  focusHandler() {
    if (!this.optShown) {
      this.optShown = true;
      PubSub.publish('region:open-select', {'clickname': 'regio-menu-open'});
    }
  }

  keydownHandler(e: KeyboardEvent) {
    this.isKeyboardArrow = (e).key !== 'Enter';
  }

  clickHandler(e: Event) {
    const select = (e.target) as HTMLSelectElement;
    const componentPath = select.getAttribute('data-component-path');

    if (componentPath) {
      Tracking2.callTealiumLink({
        businessEventType: 'userAction',
        componentPath,
        componentLabel: select.value,
        elementTagName: 'select',
        eventType: 'mousedown'
      });

      Tracking2.callTealiumLink({
        businessEventType: 'expose',
        componentPath,
        elementTagName: 'option'
      });
    }
  }

  changeHandler(e: Event) {
    if (this.isKeyboardArrow)
      return;

    const select = (e.target) as HTMLSelectElement;
    this.trySetRegionCookie(select.value);
    const selectedOpt = select.options[select.selectedIndex];
    const url = selectedOpt.getAttribute('data-target-url');
    const componentPath = selectedOpt.getAttribute('data-component-path');

    // handle brain tracking
    PubSub.publish('region:optionClick', {
      targetUrl: url,
      brainData: selectedOpt.getAttribute('data-brain-zone')
    });

    if (componentPath) {
      Tracking2.callTealiumLink({
        businessEventType: 'userAction',
        componentPath,
        componentLabel: select.value,
        elementTagName: 'option',
        eventType: 'mousedown'
      });
    }

    if (url) {
      if (this.target && this.target === '_blank') {
        window.open(url);
      } else {
        window.location.href = url;
      }
    }
  }

  trySetRegionCookie(cookieValue: string | null) {
    const expireIn3months = new Date();
    expireIn3months.setMonth(expireIn3months.getMonth() + 3);
    const secure = true;
    const domain = `.${Url.getBaseDomain()}`;

    try {
      Cookies.setItem(
        this.locationCookieName,
        cookieValue ?? '',
        expireIn3months,
        '/',
        domain,
        secure
      );
    } catch (e) {
      Logger.logUnknownError(e, this.name);
    }
  }

  /** if cookie exist update cookie for additional 3 months */
  handleRegionCookieUpdate() {
    try {
      if (Cookies.hasItem(this.locationCookieName)) {
        this.trySetRegionCookie(Cookies.getItem(this.locationCookieName));
      }
    } catch (e) {
      Logger.logUnknownError(e, this.name);
    }
  }

  /** Regio teasers must have data-brained=true therefore pub this event */
  onClickTeaserAllowTracking() {
    PubSub.publish('general:newContent', this);
  }
}
